import { BRAND_MODES, LEGACY_PARTNER_BRAND_MODE } from './brands';
import { isFoodAmigosHost } from './hosts';

export const BRAND_CACHE_STORAGE_KEY = 'WHITE_LABEL_BRAND';

const BRAND_FIELDS = [
  'mode',
  'name',
  'logoUrl',
  'circleLogoUrl',
  'splashImageUrl',
  'primaryColor',
  'language',
  'languages',
];
const KNOWN_MODES = Object.values(BRAND_MODES);

const pickBrand = (brand) => BRAND_FIELDS.reduce((picked, key) => ({ ...picked, [key]: brand[key] ?? null }), {});

/**
 * Upgrade a mode read back from storage. Only the partner mode was ever renamed;
 * anything else is passed through so an unknown value still fails `isBrand`.
 *
 * TODO(2026-10-09): remove, along with LEGACY_PARTNER_BRAND_MODE.
 */
const normaliseMode = (mode) => (mode === LEGACY_PARTNER_BRAND_MODE ? BRAND_MODES.PARTNER : mode);

const isBrand = (value) =>
  !!value && typeof value === 'object' && KNOWN_MODES.includes(value.mode) && typeof value.name === 'string';

/**
 * The last brand resolved on this browser for this (non-FoodAmigos) host.
 *
 * On the shared white-label host partners share one origin, so a logged-out visit without
 * `?partner=` (a bookmark, a typed URL, a redirect that dropped the query) has nothing to
 * brand from; instead of the neutral identity the login page keeps whatever a host, a slug
 * or a session last resolved here. On a partner's own host brand-config answers by
 * hostname, and storage is per origin, so this is only a first-paint optimisation there.
 * FoodAmigos hosts never use it: their logged-out brand is the FoodAmigos one.
 */
export function readCachedBrand(hostname = window.location.hostname) {
  if (isFoodAmigosHost(hostname)) {
    return null;
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(BRAND_CACHE_STORAGE_KEY) ?? 'null');
    // Migrate before validating: an entry written before the partner/reseller
    // rename carries the old mode, and dropping it would blank the login page's
    // branding for a partner's users. The next write stores the new value.
    const upgraded = parsed && typeof parsed === 'object' ? { ...parsed, mode: normaliseMode(parsed.mode) } : parsed;

    return isBrand(upgraded) ? pickBrand(upgraded) : null;
  } catch {
    return null;
  }
}

/**
 * Remember a brand resolved from a partner slug or a session. The FoodAmigos identity is
 * never cached: on a white-label host it must not outlive the master session that produced it.
 */
export function writeCachedBrand(brand, hostname = window.location.hostname) {
  if (!isBrand(brand) || brand.mode === BRAND_MODES.FOOD_AMIGOS || isFoodAmigosHost(hostname)) {
    return;
  }

  try {
    window.localStorage.setItem(BRAND_CACHE_STORAGE_KEY, JSON.stringify(pickBrand(brand)));
  } catch {
    // Storage unavailable (privacy mode, quota) — the current page is still branded in memory.
  }
}

export function clearCachedBrand() {
  try {
    window.localStorage.removeItem(BRAND_CACHE_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
